import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppointments } from 'api/appointment/useAppointments';
import {
  Appointment,
  AppointmentStatus,
} from 'components/_temp_ui_common/Appointments/types';
import { CalendarColorIcon } from 'components/core/icons/CalendarColorIcon';
import { H3, Text } from 'components/core/text/Text';
import { useConsult } from 'contexts/ConsultContext';
import { useNpReferral } from 'hooks/useNpReferral';
import { useNpReferralOnly } from 'hooks/useNpReferralOnly';
import { useNavigate } from 'react-router-dom';

import { AppointmentItem } from './components/AppointmentItem';
import { AppointmentScheduler } from './components/AppointmentScheduler';
import { BCMHelpButton } from './components/BCMHelpButton';
import {
  AppointmentsContainer,
  ApptList,
  ApptView,
  Board,
} from './components/styled';

interface ScheduleFirstAppointmentProps {
  memberId?: string;
  selfNpReferFlow?: boolean;
}

const ScheduleFirstAppointment: FC<ScheduleFirstAppointmentProps> = ({
  memberId,
  selfNpReferFlow = false,
}) => {
  const navigate = useNavigate();
  const consultState = useConsult();
  const patientName = consultState.consult?.patient.firstName;
  memberId = memberId || consultState.consult?.patient.id;

  const [selectedAppointment, setSelectedAppointment] = useState<Appointment>();
  const [mobileSchedulerVisible, setMobileSchedulerVisible] = useState(false);

  const isNpPlanOnly = useNpReferralOnly();
  const isNpReferral = useNpReferral();
  const isNpReferralFlow =
    isNpPlanOnly || selfNpReferFlow || isNpReferral || selfNpReferFlow;

  const { data: appointments = [] } = useAppointments(
    memberId,
    {
      program: isNpReferralFlow ? 'np' : 'core',
    },
    'member_app',
  );

  // Triggered whenever the list of appointments changes
  useEffect(() => {
    if (!appointments?.length) return;

    // If this is the initial load and there's no selected appointment, select the first unscheduled one
    const firstUnscheduled = appointments.find(
      a => a.status === AppointmentStatus.Unscheduled,
    );
    // If there's no unscheduled appointment and this is the first load, navigate to home
    if (!selectedAppointment && !firstUnscheduled) {
      navigate('/home');
    }
    if (!selectedAppointment) {
      // This is the first load, so we just pick the first unscheduled appointment
      setSelectedAppointment(firstUnscheduled);
    }
  }, [appointments]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSchedulingComplete = useCallback(() => {
    const firstUnscheduled = appointments.find(
      a => a.status === AppointmentStatus.Unscheduled,
    );
    if (firstUnscheduled) {
      setSelectedAppointment(firstUnscheduled);
      setMobileSchedulerVisible(false);
    } else {
      navigate('/home');
    }
  }, [appointments, navigate]);

  const hasCaregiverTerms = useMemo(
    () =>
      consultState.consult ? consultState.consult.patient.age < 18 : false,
    [consultState.consult],
  );

  return (
    <Board>
      <ApptList>
        <div className="flex flex-col gap-6">
          <div>
            <CalendarColorIcon />
            <H3>Let&apos;s schedule your first appointment!</H3>
          </div>
          {isNpReferralFlow ? (
            <Text variant="text4" className="pr-6" block>
              Your first appointment is a Clinical Intake, where a skilled
              psychometrist works with you to gain deeper insights into concerns
              and medical history so we can personalize the testing plan.
            </Text>
          ) : (
            <>
              <Text variant="text4" className="pr-28" block>
                Your first 45-minute meeting will be with your Behavioral Care
                Manager (BCM) to develop your care plan and meet your team.
              </Text>
              {hasCaregiverTerms && (
                <div>
                  <Text color="error" variant="subheadline3" block bold>
                    Important Note:
                  </Text>

                  <Text bold>
                    Both {patientName} AND an adult caregiver must attend this
                    first session.
                  </Text>
                </div>
              )}
            </>
          )}
        </div>
        <AppointmentsContainer>
          {appointments.map(appointment => {
            const { id, name, duration, status, scheduledStartTime } =
              appointment;

            const disabled = [
              AppointmentStatus.Blocked,
              AppointmentStatus.Scheduled,
            ].includes(status);
            const onClick = () => {
              setSelectedAppointment(appointment);
              setMobileSchedulerVisible(true); // for mobile view
            };
            return (
              <AppointmentItem
                key={id}
                name={name}
                duration={duration}
                selected={selectedAppointment?.id === id}
                disabled={disabled}
                scheduledDateTime={scheduledStartTime || undefined}
                appointmentType={appointment.appointmentType}
                status={appointment.status}
                onClick={onClick}
                credentialedName={appointment.credentialedName}
                avatarUrlSmall={appointment.avatarUrlSmall}
              />
            );
          })}
        </AppointmentsContainer>

        {!isNpReferralFlow && <BCMHelpButton />}
      </ApptList>
      <ApptView shouldHide={!mobileSchedulerVisible}>
        {selectedAppointment && (
          <AppointmentScheduler
            appointment={selectedAppointment}
            existingAppointments={appointments}
            onSchedulingComplete={onSchedulingComplete}
          />
        )}
      </ApptView>
    </Board>
  );
};

export default ScheduleFirstAppointment;
